<template>
  <div class="h-full flex flex-col relative dark:[color-scheme:dark]">
    <div class="relative h-10 shrink grow">
      <InfiniteScroll
        ref="infiniteScroll"
        :fetch="fetchMessages"
        :page-count="pageCount"
        class="h-full"
        @scroll-bottom="newMessages = false"
      >
        <template #no-more>
          {{ $t('components.message.elements.chat.noMoreMessages') }}
        </template>

        <div>
          <div v-for="(group, key) in grouped" :key="key" class="mb-8 px-4">
            <p class="text-xs text-center my-3 opacity-60">
              {{ key }}
            </p>

            <div>
              <div v-for="message in group">
                <message :message="message" :last-read="lastReadMessage" v-model:active="active" />
              </div>
            </div>
          </div>
        </div>
      </InfiniteScroll>

      <div v-if="newMessages" class="text-white bg-primary-500 dark:bg-primary-800 px-3 py-[0.35rem] rounded-full absolute bottom-4 left-1/2 -translate-x-1/2 text-sm flex items-center space-x-2 hover:scale-101 transition-all cursor-pointer" @click="scrollToBottom">
        <p>{{ $t('components.message.elements.chat.new') }}</p>
        <o-icon icon="arrowDown" size="10" />
      </div>
    </div>

    <div class="flex-shrink-0 w-full border-t">
      <send :customer="customer" @success="handleSendSuccess" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance } from 'vue'
import { useCustomerMessageStore } from '~/stores/useCustomerMessageStore'

import debounce from 'lodash/debounce'
import uniqBy from 'lodash/uniqBy'

import Message from '@/components/message/elements/Message.vue'
import Send from '@/components/message/elements/Send.vue'
import InfiniteScroll from '@/components/message/elements/InfiniteScroll.vue'

const { $api, $bus, $date } = useNuxtApp()

const customerMessageStore = useCustomerMessageStore()
const uid = getCurrentInstance()?.uid

interface Props {
  idCustomer: string | number
}

const props = defineProps<Props>()

const customer = ref<any>(null)
const grouped = ref<Record<string, any[]>>({})
const messages = ref<any[]>([])
const ids = ref<any[]>([])
const active = ref<number | null>(null)
const pageCount = ref<number | null>(null)
const newMessages = ref(false)
const infiniteScroll = ref<any>()

const lastReadMessage = computed(() => {
  return messages.value.find(message => message.read)
})

const fetchCounts = () => customerMessageStore.fetchCounts()

const setMessages = (msgs: any[]) => {
  const unique = uniqBy(msgs, 'idCustomerMessage')

  const sorted = unique
    .sort((a: any, b: any) => a.created - b.created)

  const groupedResult = sorted
    .reduce((acc: Record<string, any[]>, cur: any) => {
      const date = $date.format(cur.created * 1000, 'dd MMM yyyy')
      acc[date] = [...acc[date] || [], cur]
      return acc
    }, {})

  const idsList = sorted.map((message: any) => message.idCustomerMessage)

  grouped.value = groupedResult
  messages.value = sorted
  ids.value = idsList

  fetchCounts()
}

const scrollToBottom = () => {
  if (infiniteScroll.value?.$el) {
    newMessages.value = false

    const position = 100000

    nextTick(() => {
      infiniteScroll.value.$el.scrollTo({ top: position, behavior: 'smooth' })
    })
  }
}

const appendCustomerMessages = (msgs: any[], { scroll = true } = {}) => {
  setMessages([...messages.value, ...msgs])

  if (scroll) {
    scrollToBottom()
  } else {
    const el = infiniteScroll.value?.$el

    if (el) {
      const scrollDistanceTotal = el.scrollHeight - el.clientHeight
      const scrollDistanceFromBottom = scrollDistanceTotal - el.scrollTop

      if (scrollDistanceFromBottom > 20) {
        newMessages.value = true
      }
    }
  }
}

const reload = debounce((scroll = true) => {
  if (infiniteScroll.value) {
    infiniteScroll.value.reload(scroll)
  }
}, 50)

const fetchMessages = async (page: number) => {
  try {
    const response = await $api.customerMessage.customer(props.idCustomer, {
      page,
    })

    if (response.items && response.items.length > 0) {
      if (page === 1) {
        setMessages(response.items)
      } else {
        setMessages([...response.items, ...messages.value])
      }
    }

    customer.value = response.customer

    pageCount.value = response.paging.pageCount
  } catch (error) {
    console.log(error)
  }
}

const fetchMessage = debounce(async (idCustomerMessage: string | number) => {
  try {
    const response = await $api.customerMessage.get(idCustomerMessage)

    if (response.data) {
      appendCustomerMessages([response.data], {
        scroll: false,
      })
    }
  } catch (error) {
    console.log(error)
  }
}, 500)

const handleSendSuccess = (message: any) => {
  appendCustomerMessages([message])
}

watch(() => props.idCustomer, (newIdCustomer, oldIdCustomer) => {
  if (newIdCustomer !== oldIdCustomer) {
    messages.value = []
    grouped.value = {}
    ids.value = []

    reload()
  }
})

onMounted(() => {
  $bus.on.created('CustomerMessage', uid, async (data: any) => {
    if (data.idCustomer === props.idCustomer) {
      await fetchMessage(data.idCustomerMessage)
      fetchCounts()
    }
  })

  $bus.on.updated('CustomerMessage', uid, async (data: any) => {
    if (data.idCustomer === props.idCustomer) {
      const messageId = data.idCustomerMessage

      for (const m of messages.value) {
        if (m.idCustomerMessage == messageId) {
          m.channelApp = data.channelApp
          m.channelEmail = data.channelEmail
          m.channelSms = data.channelSms
        }
      }
    }
  })
})

onBeforeUnmount(() => {
  $bus.off.all(uid)
})
</script>
